import React from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Divider, 
  Chip, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import PageTemplate from '../components/PageTemplate';

const CareersPage: React.FC = () => {
  // Mock data for job listings
  const openPositions = [
    {
      title: 'Frontend Developer',
      department: 'Engineering',
      location: 'Remote (US, Europe)',
      type: 'Full-time',
      description: 'We are looking for a frontend developer with experience in React and TypeScript to help build and improve our user interfaces.',
      requirements: [
        'Proficiency in React and TypeScript',
        'Experience with state management libraries (Redux, MobX, etc.)',
        'Knowledge of modern CSS techniques and frameworks',
        '3+ years of professional experience in frontend development'
      ]
    },
    {
      title: 'DevOps Engineer',
      department: 'Operations',
      location: 'Remote (Worldwide)',
      type: 'Full-time',
      description: 'Join our operations team to help scale and maintain our infrastructure, ensuring a reliable and secure platform for our users.',
      requirements: [
        'Experience with cloud providers (AWS, GCP, Azure)',
        'Knowledge of containerization and orchestration (Docker, Kubernetes)',
        'Proficiency with CI/CD pipelines',
        'Understanding of infrastructure as code (Terraform, CloudFormation)'
      ]
    },
    {
      title: 'Customer Support Specialist',
      department: 'Support',
      location: 'Remote (APAC timezone)',
      type: 'Part-time',
      description: 'Help our users resolve issues and get the most out of our temporary email service with stellar customer support.',
      requirements: [
        'Excellent written communication skills',
        'Experience with helpdesk software',
        'Ability to work in APAC-friendly hours',
        'Passion for helping users and solving problems'
      ]
    }
  ];

  // Company values
  const companyValues = [
    {
      title: 'Privacy First',
      description: 'We believe privacy is a fundamental right. Every product decision we make prioritizes user privacy and data security.'
    },
    {
      title: 'Transparency',
      description: 'We\'re clear about how our service works, what data we collect (minimal), and how we use it. No hidden agendas or fine print.'
    },
    {
      title: 'Accessibility',
      description: 'Our tools should be accessible to everyone, regardless of technical expertise or location in the world.'
    },
    {
      title: 'Innovation',
      description: 'We constantly explore new ways to improve privacy and security, staying ahead of threats and challenges.'
    }
  ];

  return (
    <PageTemplate 
      title="Careers at 1TimeMail" 
      description="Join our team and help build the future of private, secure communication."
      breadcrumbs={[{ label: 'Careers', path: '/careers' }]}
    >
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Join Our Team
        </Typography>
        <Typography variant="body1" paragraph>
          At 1TimeMail, we're on a mission to make online privacy accessible to everyone. 
          We're a distributed team of privacy advocates, engineers, and product specialists 
          who are passionate about building tools that protect users online.
        </Typography>
        <Typography variant="body1" paragraph>
          We offer competitive salaries, flexible work arrangements, and a supportive 
          culture that values work-life balance. If you're excited about making an impact 
          in the privacy space, we'd love to hear from you!
        </Typography>
      </Box>

      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Our Values
        </Typography>
        <Grid container spacing={3}>
          {companyValues.map((value, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Benefits & Perks
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Remote-First Culture" 
                  secondary="Work from anywhere with a flexible schedule that works for you."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Competitive Compensation" 
                  secondary="We offer salaries based on experience and location, with equity options."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Health & Wellness" 
                  secondary="Comprehensive health coverage and wellness stipend."
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Professional Development" 
                  secondary="Annual learning budget for courses, books, and conferences."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Work-Life Balance" 
                  secondary="Generous PTO policy, plus company-wide wellness days."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Home Office Setup" 
                  secondary="Stipend to help you create your ideal workspace."
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 8 }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Open Positions
        </Typography>
        {openPositions.map((position, index) => (
          <Card 
            key={index} 
            elevation={0}
            sx={{ 
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {position.title}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {position.department}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {position.location}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <TimerIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {position.type}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button 
                  variant="outlined" 
                  color="primary"
                  sx={{ mt: { xs: 2, sm: 0 } }}
                >
                  Apply Now
                </Button>
              </Box>

              <Typography variant="body2" paragraph>
                {position.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Requirements
              </Typography>
              <Grid container spacing={1}>
                {position.requirements.map((req, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {req}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Don't See a Match?
        </Typography>
        <Typography variant="body1" paragraph>
          We're always interested in connecting with talented individuals, even if we don't have a 
          specific position open that matches your skills. Send your resume to 
          <Typography component="span" fontWeight={600} color="primary.main"> careers@1timemail.org</Typography> and 
          tell us how you could contribute to our mission!
        </Typography>
      </Box>
    </PageTemplate>
  );
};

export default CareersPage; 